import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File as FileBoxIcon, Trash, Eye } from 'lucide-react';

export default function CsvUpload({ onChange }: { onChange?: (file: File | null) => void }) {
  const [file, setFile] = useState<File | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const selected = acceptedFiles[0];
    if (!selected) return;

    setFile(selected);
    onChange?.(selected);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'application/pdf': ['.pdf'],
    },
  });
  const handlePreview = () => {
    if (!file) return;

    // PDF preview
    if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
      const url = URL.createObjectURL(file);

      window.open(url, '_blank');

      return;
    }

    // CSV preview
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;

      const rows = text.split('\n').map((row) => row.split(','));

      const tableRows = rows
        .map(
          (row) => `
          <tr>
            ${row
              .map(
                (cell) => `
                  <td style="border:1px solid #444;padding:8px;">
                    ${cell}
                  </td>
                `
              )
              .join('')}
          </tr>
        `
        )
        .join('');

      const newWindow = window.open('', '_blank');

      if (newWindow) {
        newWindow.document.write(`
        <html>
          <head>
            <title>CSV Preview</title>
          </head>

          <body style="background:#111;color:white;padding:20px;font-family:sans-serif;">
            <h2>CSV Preview</h2>

            <table style="border-collapse:collapse;width:100%;">
              ${tableRows}
            </table>
          </body>
        </html>
      `);

        newWindow.document.close();
      }
    };

    reader.readAsText(file);
  };
  const removeFile = () => {
    setFile(null);
    onChange?.(null);
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`flex flex-col items-center justify-center gap-2
        border border-dashed border-white/20 rounded-lg p-6 cursor-pointer
        hover:border-white/40 transition ${isDragActive ? 'bg-white/5' : ''}`}
      >
        <input {...getInputProps()} />

        <Upload className="w-5 h-5 text-gray-400" />
        <p className="font-16 font-medium">Upload CSV </p>
        <p className="text-sm text-gray-400">
          Drag & drop CSV or <span className="text-blue-400">browse</span>
        </p>
      </div>
      <p className="text-sm text-gray-400">Supported formats: .csv </p>

      {file && (
        <div className="flex items-center justify-between bg-[#356D28] border border-white/10 p-3 rounded-md">
          <div className="flex items-center gap-2">
            <FileBoxIcon className="w-5 h-5 text-[#FFFFFF]" />
            <p className="font-14">{file.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handlePreview}>
              <Eye className="w-4 h-4 text-white hover:text-blue-300 transition" />
            </button>

            <button onClick={removeFile}>
              <Trash className="w-4 h-4 text-[#FF0000]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
